import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Check, ChevronDown, Lock, RefreshCw, X } from 'lucide-react';
import { apiClient } from '../../api';
import Drawer from '../UI/Drawer';
import { monoLabel, primaryBtn, primaryBtnStyle, secondaryBtn, secondaryBtnStyle, ghostBtn, ghostBtnStyle, errMessage } from '../UI/ink';
import {
  HOST_TOOLS, RUN_STATE, TARGET_LABEL, barColor, fmtDur, matchAsset, parseToolCalls, phaseState, readableOutput, runState, stamp, targetHref,
} from './model';

const Label = ({ children }) => <span className={monoLabel} style={{ color: 'var(--ink-faint)' }}>{children}</span>;

const ICON = {
  done: { bg: 'var(--surf-raised)', b: 'var(--surf-line-strong)', fg: 'var(--sev-good-fg)' },
  running: { bg: 'var(--surf-bg)', b: 'var(--ink-text)', fg: 'var(--ink-text)' },
  gate: { bg: 'var(--sev-high-bg)', b: 'var(--sev-high-fg)', fg: 'var(--sev-high-fg)' },
  failed: { bg: 'var(--sev-crit-bg)', b: 'var(--sev-crit-fg)', fg: 'var(--sev-crit-fg)' },
  rejected: { bg: 'var(--sev-low-bg)', b: 'var(--sev-low-fg)', fg: 'var(--sev-low-fg)' },
};

const PhaseIcon = ({ st, n, isGate }) => {
  const ic = ICON[st] || { bg: 'transparent', b: 'var(--surf-line)', fg: isGate ? 'var(--ink-muted)' : 'var(--ink-faint)' };
  return (
    <span className="w-6 h-6 shrink-0 box-border flex items-center justify-center rounded-full border font-mono text-[11px]" style={{ background: ic.bg, borderColor: ic.b, color: ic.fg }}>
      {st === 'done' && <Check className="w-3 h-3" strokeWidth={3} />}
      {st === 'running' && <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: 'currentColor' }} />}
      {(st === 'gate' || st === 'rejected' || (st === 'pending' && isGate)) && <Lock className="w-3 h-3" strokeWidth={2.4} />}
      {st === 'failed' && <X className="w-[11px] h-[11px]" strokeWidth={3} />}
      {st === 'pending' && !isGate && n}
    </span>
  );
};

const PhaseBody = ({ p, raw, servers = [] }) => {
  const out = readableOutput(p.output);
  const tools = parseToolCalls(p.tool_calls);
  const queued = tools.some(t => HOST_TOOLS.includes(t.name));
  return (
    <div className="flex flex-col gap-3 pl-[50px] pr-3.5 pb-3.5">
      {p.approval_status === 'approved' && p.approval_note && p.approval_note !== 'null' && (
        <p className="m-0 text-[12.5px] leading-normal" style={{ color: 'var(--ink-muted)' }}><span style={{ color: 'var(--ink-faint)' }}>Approval note · </span>{p.approval_note}</p>
      )}
      {out.summary && <p className="m-0 text-[13px] leading-relaxed whitespace-pre-wrap" style={{ color: 'var(--ink-muted)' }}>{out.summary}</p>}
      {p.error && <code className="block px-3 py-2 rounded-lg font-mono text-[11.5px] leading-normal whitespace-pre-wrap break-words" style={{ background: 'var(--sev-crit-bg)', color: 'var(--ink-text)' }}>{p.error}</code>}
      {out.facts.length > 0 && (
        <div className="flex flex-col rounded-[9px] border overflow-hidden" style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line)' }}>
          {out.facts.map(([k, v]) => (
            <div key={k} className="grid grid-cols-[120px_minmax(0,1fr)] gap-2.5 px-3 py-[7px] border-b last:border-b-0" style={{ borderColor: 'var(--surf-line)' }}>
              <span className="text-xs" style={{ color: 'var(--ink-faint)' }}>{k}</span>
              <span className="text-xs break-words">{v}</span>
            </div>
          ))}
        </div>
      )}
      {out.lists.map(([k, items]) => (
        <div key={k} className="flex flex-col gap-1">
          <Label>{k.toUpperCase()}</Label>
          <ul className="m-0 pl-4 flex flex-col gap-0.5 text-[12.5px] leading-normal" style={{ color: 'var(--ink-muted)' }}>
            {items.map((t, i) => {
              if (!/asset/i.test(k)) return <li key={i}>{t}</li>;
              const s = matchAsset(t, servers);
              return (
                <li key={i}>
                  {t}{' '}
                  {s ? (
                    <Link to={`/assets/${s.id}`} aria-label={`Open asset ${s.hostname || s.ip_address}`} title="Registered asset" className="inline-flex align-[-1px] hover:opacity-70" style={{ color: 'var(--ink-link)' }}>
                      <ArrowUpRight className="w-3 h-3" strokeWidth={2} />
                    </Link>
                  ) : (
                    <span title="Not in this organisation's asset inventory" className="font-mono text-[10px] whitespace-nowrap" style={{ color: 'var(--ink-faint)' }}>· unregistered</span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ))}
      {tools.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <Label>TOOL CALLS · {tools.length}</Label>
          {tools.map((tc, i) => (
            <div key={i} className="flex flex-col gap-1 px-[11px] py-2 rounded-[9px] border" style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line)' }}>
              <span className="flex items-baseline gap-2 font-mono text-xs min-w-0">
                <span className="font-medium shrink-0">{tc.name}</span>
                <span className="truncate" style={{ color: 'var(--ink-faint)' }} title={tc.args}>{tc.args}</span>
              </span>
              {tc.res && <span className="flex gap-[7px] text-[12.5px] break-words" style={{ color: 'var(--ink-muted)' }}><span aria-hidden="true" style={{ color: 'var(--ink-faint)' }}>→</span>{tc.res}</span>}
            </div>
          ))}
          {queued && <Link to="/response-actions" className="self-start text-[12.5px] hover:underline" style={{ color: 'var(--ink-link)' }}>Review queued actions in Response Actions</Link>}
        </div>
      )}
      {out.rec.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <Label>RECOMMENDED ACTIONS</Label>
          {out.rec.map((t, i) => <span key={i} className="text-[12.5px] leading-normal">{t}</span>)}
        </div>
      )}
      {raw.on && (
        <pre className="m-0 px-3 py-2.5 rounded-[9px] border font-mono text-[11px] leading-relaxed whitespace-pre-wrap break-all max-h-[320px] overflow-y-auto" style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line)', color: 'var(--ink-muted)' }}>
          {p.output || p.error || '(no output)'}{p.tool_calls && p.tool_calls !== 'null' && p.tool_calls !== '[]' ? `\n\n— tool_calls —\n${p.tool_calls}` : ''}
        </pre>
      )}
      <span><button type="button" onClick={raw.toggle} aria-pressed={raw.on} className="h-[26px] px-2 rounded-md border font-mono text-[10.5px]" style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-muted)' }}>{raw.on ? 'Readable' : 'Raw output'}</button></span>
    </div>
  );
};

// An email the agent wrote to the organisation's incident contacts (notify_contacts).
const EMAIL_STATUS = {
  pending: ['Waiting for approval', 'var(--sev-med-fg)'],
  sending: ['Sending…', 'var(--sev-med-fg)'],
  sent: ['Sent', 'var(--sev-good-fg)'],
  failed: ['Failed', 'var(--sev-crit-fg)'],
  discarded: ['Discarded', 'var(--ink-faint)'],
};

const ContactEmail = ({ email, canApprove, busy, nameOf, onDecide }) => {
  const [label, dot] = EMAIL_STATUS[email.status] || [email.status, 'var(--ink-faint)'];
  const open = email.status === 'pending' || email.status === 'failed';
  return (
    <div className="flex flex-col gap-2 px-3.5 py-3 rounded-[10px] border" style={{ borderColor: email.status === 'pending' ? 'var(--accent)' : 'var(--surf-line)' }}>
      <span className="flex items-center gap-2 text-[12.5px]"><span className="w-[7px] h-[7px] rounded-full shrink-0" style={{ background: dot }} />
        <span className="font-semibold">{label}</span>
        {email.decided_by && email.status !== 'pending' && <span style={{ color: 'var(--ink-faint)' }}>· {email.status === 'discarded' ? 'discarded' : 'approved'} by {nameOf(email.decided_by)}</span>}
      </span>
      <span className="text-[12.5px]" style={{ color: 'var(--ink-muted)' }}>To <span className="font-mono text-xs" style={{ color: 'var(--ink-text)' }}>{(email.recipients || []).join(', ')}</span> <span style={{ color: 'var(--ink-faint)' }}>({(email.roles || []).join(', ')})</span></span>
      <span className="text-[13.5px] font-semibold">{email.subject}</span>
      <span className="text-[12.5px] leading-normal whitespace-pre-wrap break-words" style={{ color: 'var(--ink-muted)' }}>{email.body}</span>
      {email.error && <span className="text-[12.5px]" style={{ color: 'var(--sev-crit-fg)' }}>{email.error}</span>}
      {open && (canApprove ? (
        <span className="flex flex-wrap items-center gap-2">
          <button type="button" disabled={busy} onClick={() => onDecide(email, true)} className={primaryBtn} style={primaryBtnStyle}>{busy ? 'Working…' : email.status === 'failed' ? 'Retry send' : 'Approve and send'}</button>
          <button type="button" disabled={busy} onClick={() => onDecide(email, false)} className={secondaryBtn} style={secondaryBtnStyle}>Discard</button>
        </span>
      ) : <span className="text-[12.5px]" style={{ color: 'var(--ink-faint)' }}>You need the playbooks:approve permission to send or discard this email.</span>)}
    </div>
  );
};

const RunDrawer = ({ runId, workflows, agentName, agentTools = () => [], nameOf, servers, canApprove, onClose, onChanged, onRunAgain }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [openPh, setOpenPh] = useState(null);
  const [raw, setRaw] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [emailBusy, setEmailBusy] = useState(null);
  const timer = useRef(null);

  const load = useCallback(async () => {
    if (!runId) return;
    try {
      const res = await apiClient.get(`/api/workflows/runs/${runId}`);
      setData(res.data);
      setError(null);
    } catch (err) {
      setError(errMessage(err, 'Could not load this run.'));
    }
  }, [runId]);

  useEffect(() => { setData(null); setOpenPh(null); setRejecting(false); setReason(''); load(); }, [load]);

  // Poll while the run can still change.
  const status = data?.run?.status;
  useEffect(() => {
    clearTimeout(timer.current);
    if (!runId || !status) return undefined;
    if (status === 'running' || status === 'waiting_approval') {
      timer.current = setTimeout(load, status === 'running' ? 3000 : 10000);
    }
    return () => clearTimeout(timer.current);
  }, [runId, status, data, load]);

  const run = data?.run;
  const wf = run ? workflows.find(w => w.id === run.workflow_id) : null;
  const defs = wf?.phases || [];
  const order = (p) => {
    const i = defs.findIndex(d => d.phase_id === p.phase_id);
    return i < 0 ? 999 : i;
  };
  const phases = (data?.phases || []).slice().sort((a, b) => order(a) - order(b));
  const gateIdx = run?.status === 'waiting_approval' ? phases.findIndex(p => p.status === 'approval_required') : -1;
  const states = phases.map((p, i) => phaseState(p, run || {}, i === gateIdx));
  const st = run ? runState({ ...run, rejected: phases.some(p => p.approval_status === 'rejected') ? 1 : 0 }) : 'running';
  const RS = RUN_STATE[st];
  const doneN = states.filter(s => s === 'done').length;
  const agentMs = phases.reduce((t, p) => t + (Number(p.duration_ms) || 0), 0);
  const defaultOpen = st === 'failed' ? states.indexOf('failed')
    : st === 'waiting' ? -1
    : st === 'stopped' ? states.indexOf('rejected') - 1
    : st === 'running' ? states.indexOf('running') - 1
    : phases.length - 1;
  const openIdx = openPh ?? defaultOpen;
  const href = run ? targetHref(run.target_type, run.target_id, servers) : null;
  const gate = gateIdx >= 0 ? phases[gateIdx] : null;
  // Tools come from the gate phase's agent, not the phase.
  const gateTools = gate ? agentTools(gate.agent_id) : [];
  const hostTools = gateTools.filter(t => HOST_TOOLS.includes(t));
  const earlierRec = gate ? phases.slice(0, gateIdx).flatMap(p => readableOutput(p.output).rec) : [];
  const assetHost = run?.target_type === 'asset' ? run.target_id : null;

  const decide = async (approved) => {
    if (!gate) return;
    setBusy(true);
    try {
      await apiClient.post(`/api/workflows/runs/${run.id}/phases/${gate.id}/approve`, approved ? { approved: true, note: note.trim() || undefined } : { approved: false, reason: reason.trim() });
      setRejecting(false);
      setReason('');
      setNote('');
      await load();
      onChanged();
    } catch (err) {
      setError(errMessage(err, approved ? 'Could not approve.' : 'Could not reject.'));
    } finally {
      setBusy(false);
    }
  };

  const decideEmail = async (email, approve) => {
    setEmailBusy(email.id);
    try {
      await apiClient.post(`/api/workflows/runs/${run.id}/emails/${email.id}/decide`, { approve });
      await load();
    } catch (err) {
      setError(errMessage(err, approve ? 'Could not send the email.' : 'Could not discard the email.'));
    } finally {
      setEmailBusy(null);
    }
  };

  const live = status === 'running' || status === 'waiting_approval';

  return (
    <Drawer
      open={!!runId}
      kicker={`RUN · ${runId ? runId.slice(0, 8).toUpperCase() : ''}`}
      title={run?.workflow_name || wf?.name || 'Playbook run'}
      onClose={onClose}
      footer={run && (
        <>
          <div className="flex items-center gap-3">
            {wf && <button type="button" onClick={() => onRunAgain(run)} className={secondaryBtn} style={secondaryBtnStyle}><RefreshCw className="w-3.5 h-3.5" strokeWidth={1.8} />Run again</button>}
            {href && <Link to={href} className="text-[13px] hover:underline" style={{ color: 'var(--ink-link)' }}>Open {(TARGET_LABEL[run.target_type] || 'target').toLowerCase()}</Link>}
          </div>
          <span className="flex items-center gap-[7px] text-xs" style={{ color: 'var(--ink-faint)' }}>
            <span className={`w-1.5 h-1.5 rounded-full ${live ? 'animate-pulse' : ''}`} style={{ background: live ? 'var(--sev-good-fg)' : 'var(--surf-line-strong)' }} />
            {live ? 'Updates live' : 'Finished'}
          </span>
        </>
      )}
    >
      {error && <div role="alert" className="mb-4 px-3.5 py-2.5 rounded-[10px] border text-[13px]" style={{ borderColor: 'var(--sev-crit-fg)', color: 'var(--sev-crit-fg)', background: 'var(--sev-crit-bg)' }}>{error}</div>}
      {!run ? (
        !error && <p className="m-0 text-sm" style={{ color: 'var(--ink-faint)' }}>Loading run…</p>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-2.5">
            <span className="inline-flex items-center gap-[7px] h-6 px-[9px] rounded-md font-mono text-[10.5px] font-medium tracking-[0.06em]" style={{ background: RS.chip }}>
              <span className="w-[7px] h-[7px] rounded-[1px] box-border" style={{ background: RS.mk, border: `1px solid ${RS.mkB || 'transparent'}` }} />
              {RS.label.toUpperCase()}
            </span>
            <span className="font-mono text-[11.5px]" style={{ color: 'var(--ink-faint)' }}>{doneN} of {phases.length} phases done</span>
          </div>

          <div className="grid grid-cols-[92px_minmax(0,1fr)] gap-y-[7px] gap-x-3 text-[13px]">
            <span style={{ color: 'var(--ink-faint)' }}>Target</span>
            <span className="min-w-0 truncate">
              {run.target_type && run.target_id ? (
                <>
                  <span style={{ color: 'var(--ink-faint)' }}>{TARGET_LABEL[run.target_type] || run.target_type} </span>
                  {href ? <Link to={href} className="font-mono text-xs hover:underline" style={{ color: 'var(--ink-link)' }}>{run.target_id}</Link> : <span className="font-mono text-xs">{run.target_id}</span>}
                </>
              ) : <span style={{ color: 'var(--ink-muted)' }}>None</span>}
            </span>
            {run.target_context && run.target_context !== 'null' && (
              <>
                <span style={{ color: 'var(--ink-faint)' }}>Context</span>
                <span className="text-[12.5px] leading-normal whitespace-pre-wrap break-words" style={{ color: 'var(--ink-muted)' }}>{run.target_context}</span>
              </>
            )}
            {assetHost && (
              <>
                <span style={{ color: 'var(--ink-faint)' }}>Asset</span>
                <span className="font-mono text-xs">{assetHost}</span>
              </>
            )}
            <span style={{ color: 'var(--ink-faint)' }}>Started</span>
            <span style={{ color: 'var(--ink-muted)' }}>{stamp(run.started_at)}{run.started_by ? ` by ${nameOf(run.started_by)}` : ''}</span>
            <span style={{ color: 'var(--ink-faint)' }}>Agent time</span>
            <span className="font-mono text-xs" style={{ color: 'var(--ink-muted)' }}>{agentMs ? fmtDur(agentMs) : '—'}</span>
            {typeof run.cost_usd === 'number' && (
              <>
                <span style={{ color: 'var(--ink-faint)' }}>Est. cost</span>
                <span className="font-mono text-xs" style={{ color: 'var(--ink-muted)' }} title="Estimated from token usage, as in Settings › AI usage">${run.cost_usd < 0.01 ? run.cost_usd.toFixed(4) : run.cost_usd.toFixed(2)}</span>
              </>
            )}
          </div>

          <div className="flex gap-1" aria-hidden="true">
            {states.map((s, i) => <span key={i} className="flex-grow h-1 rounded-sm" style={{ background: barColor(s) }} />)}
          </div>

          <div className="flex flex-col gap-2">
            <Label>PHASES</Label>
            {phases.map((p, i) => {
              const s = states[i];
              const def = defs.find(d => d.phase_id === p.phase_id);
              const isGateDef = !!(def?.approval_required || Number(p.approval_required));
              const canOpen = s === 'done' || s === 'failed';
              const open = canOpen && openIdx === i;
              const SUB = { done: 'Completed', running: 'Running now', gate: 'Needs your approval', pending: isGateDef ? 'Approval gate · not reached' : 'Not started', failed: 'Failed', rejected: 'Rejected · run stopped' };
              const approved = p.approval_status === 'approved';
              return (
                <div key={p.id} className="rounded-xl border" style={{ borderColor: s === 'gate' ? 'var(--sev-high-fg)' : 'var(--surf-line)', background: s === 'gate' ? 'var(--surf-raised)' : 'var(--surf-surface)' }}>
                  <button type="button" onClick={() => canOpen && setOpenPh(open ? -1 : i)} aria-expanded={canOpen ? open : undefined} disabled={!canOpen} className="w-full min-h-[54px] box-border flex items-center gap-3 px-3.5 text-left disabled:cursor-default">
                    <PhaseIcon st={s} n={i + 1} isGate={isGateDef} />
                    <span className="flex-grow min-w-0 flex flex-col gap-[3px] py-2">
                      <span className="text-[13.5px] font-medium" style={{ color: s === 'pending' ? 'var(--ink-muted)' : 'var(--ink-text)' }}>{p.phase_name}</span>
                      <span className="font-mono text-[11px] truncate" style={{ color: 'var(--ink-faint)' }}>{agentName(p.agent_id)} · {SUB[s]}{approved && s === 'done' ? ' · approved' : ''}</span>
                    </span>
                    <span className="font-mono text-[11.5px] shrink-0" style={{ color: 'var(--ink-faint)' }}>{fmtDur(p.duration_ms)}</span>
                    {canOpen && <ChevronDown className={`w-3.5 h-3.5 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} strokeWidth={1.8} style={{ color: 'var(--ink-faint)' }} />}
                  </button>

                  {open && <PhaseBody p={p} raw={{ on: raw, toggle: () => setRaw(v => !v) }} servers={servers} />}

                  {s === 'gate' && (
                    <div className="flex flex-col gap-3 pl-[50px] pr-3.5 pb-4">
                      <span className="text-sm font-semibold">{p.phase_name} is waiting for your approval</span>
                      <p className="m-0 text-[13px] leading-relaxed" style={{ color: 'var(--ink-muted)' }}>
                        {hostTools.length ? (
                          <>Approving lets the {agentName(p.agent_id)} agent call {hostTools.map((t, k) => <span key={t}>{k > 0 && ' and '}<span className="font-mono text-xs" style={{ color: 'var(--ink-text)' }}>{t}</span></span>)}. Each call is queued as a pending action in Response Actions. Nothing reaches a host until someone confirms it there.</>
                        ) : gateTools.length ? (
                          <>Approving lets the {agentName(p.agent_id)} agent run with {gateTools.map((t, k) => <span key={t}>{k > 0 && ', '}<span className="font-mono text-xs" style={{ color: 'var(--ink-text)' }}>{t}</span></span>)}. The rest of the playbook continues after it.</>
                        ) : (
                          <>Approving lets the {agentName(p.agent_id)} agent run. It has no tools, so it only works from earlier phases’ output. The rest of the playbook continues after it.</>
                        )}
                      </p>
                      {earlierRec.length > 0 && (
                        <div className="flex flex-col gap-1.5 px-3 py-2.5 rounded-[9px] border" style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line)' }}>
                          <Label>EARLIER PHASES RECOMMENDED</Label>
                          {earlierRec.map((t, k) => <span key={k} className="text-[12.5px] leading-normal">{t}</span>)}
                        </div>
                      )}
                      {!canApprove ? (
                        <span className="text-[12.5px]" style={{ color: 'var(--ink-faint)' }}>You need the playbooks:approve permission to answer this gate.</span>
                      ) : !rejecting ? (
                        <div className="flex flex-col gap-2">
                        <input
                          aria-label="Approval note (optional)"
                          value={note}
                          onChange={e => setNote(e.target.value)}
                          maxLength={1000}
                          placeholder="Note (optional) · e.g. Legal (J. Lim) approved in writing, 22 Oct"
                          className="h-9 box-border px-[11px] rounded-[9px] border text-[12.5px] outline-none"
                          style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line)', color: 'var(--ink-text)' }}
                        />
                        <div className="flex flex-wrap items-center gap-2">
                          <button type="button" disabled={busy} onClick={() => decide(true)} className={primaryBtn} style={primaryBtnStyle}>{busy ? 'Approving…' : `Approve and run ${p.phase_name}`}</button>
                          <button type="button" disabled={busy} onClick={() => setRejecting(true)} className={secondaryBtn} style={secondaryBtnStyle}>Reject…</button>
                        </div>
                        </div>
                      ) : (
                        <div className="flex flex-col gap-2">
                          <label htmlFor="reject-why" className="text-[13px] font-medium">Why are you rejecting it? <span className="font-normal" style={{ color: 'var(--ink-faint)' }}>Saved on the run and shown in history.</span></label>
                          <textarea id="reject-why" rows={3} autoFocus value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. the process is a known internal tool; confirming with the owner" className="box-border px-[11px] py-[9px] rounded-[9px] border text-[13px] leading-normal resize-none outline-none" style={{ background: 'var(--surf-bg)', borderColor: 'var(--accent)', color: 'var(--ink-text)' }} />
                          <div className="flex items-center gap-2">
                            <button type="button" disabled={busy || !reason.trim()} onClick={() => decide(false)} className="h-11 px-4 rounded-[10px] text-sm font-semibold disabled:opacity-45" style={{ background: 'var(--sev-crit-fg)', color: '#0A0A0A' }}>{busy ? 'Rejecting…' : 'Reject and stop the run'}</button>
                            <button type="button" onClick={() => { setRejecting(false); setReason(''); }} className={ghostBtn} style={ghostBtnStyle}>Cancel</button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {s === 'rejected' && (
                    <div className="flex flex-col gap-[5px] ml-[50px] mr-3.5 mb-3.5 px-3 py-2.5 rounded-[9px] border" style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line)' }}>
                      <span className="text-[12.5px]"><span className="font-semibold">Rejected{p.approval_by ? ` by ${nameOf(p.approval_by)}` : ''}</span>{p.approval_at && <span className="font-mono text-[11px]" style={{ color: 'var(--ink-faint)' }}> · {stamp(p.approval_at)}</span>}</span>
                      {run.error && run.error !== 'null' && <span className="text-[12.5px] leading-normal" style={{ color: 'var(--ink-muted)' }}>{run.error}</span>}
                    </div>
                  )}

                  {s === 'running' && approved && (
                    <div className="flex items-center gap-2.5 ml-[50px] mr-3.5 mb-3.5 px-3 py-2.5 rounded-[9px] border text-[12.5px]" style={{ background: 'var(--surf-bg)', borderColor: 'var(--surf-line)', color: 'var(--ink-muted)' }}>
                      <span className="flex-grow">Approved{p.approval_by ? ` by ${nameOf(p.approval_by)}` : ''}. {p.phase_name} is running; anything it queues appears as pending in</span>
                      <Link to="/response-actions" className="whitespace-nowrap hover:underline" style={{ color: 'var(--ink-link)' }}>Response Actions</Link>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {(data?.emails || []).length > 0 && (
            <div className="flex flex-col gap-2">
              <Label>EMAILS TO INCIDENT CONTACTS</Label>
              {data.emails.map(e => (
                <ContactEmail key={e.id} email={e} canApprove={canApprove} busy={emailBusy === e.id} nameOf={nameOf} onDecide={decideEmail} />
              ))}
            </div>
          )}
        </div>
      )}
    </Drawer>
  );
};

export default RunDrawer;
